import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { RefundAction, Service, ServiceFunction, ServiceOrder, ServiceOrderStatus } from './services.types';
import { cloneDeep } from 'lodash-es';
import { PaginatedData, PaymentType } from 'app/shared/shared.types';

@Injectable({
    providedIn: 'root'
})
export class ServicesService {
    private _services: Service[];
    private _servicesSubject: BehaviorSubject<Service[]> = new BehaviorSubject(null);
    private _paginatedOrdersSubject: BehaviorSubject<PaginatedData<ServiceOrder>> = new BehaviorSubject(PaginatedData.EMPTY);
    private _pendingCount: BehaviorSubject<number> = new BehaviorSubject(0);

    constructor(private _httpClient: HttpClient) {
    }

    get services$(): Observable<Service[]> {
        return this._servicesSubject.asObservable();
    }

    get paginatedOrders$() {
        return this._paginatedOrdersSubject.asObservable();
    }

    get promotionServices(): Service[] {
        return this.getServiceByType('promotion');
    }

    get distributionServices(): Service[] {
        return this.getServiceByType('distribution');
    }

    get tools(): Service[] {
        return this.getServiceByType('tool');
    }

    get pendingCount$(): Observable<number> {
        return this._pendingCount.asObservable();
    }

    getServices(searchQuery?: string): Observable<Service[]> {
        return this._httpClient.get('api/service/all', {
            params: {search: searchQuery}
        }).pipe(
            tap((response: Service[]) => {
                this._servicesSubject.next(response);
                this._services = response;
            })
        );
    }

    getServicesByFunctionType(type: string): Observable<Service[]> {
        return this._httpClient.get<Service[]>('api/service/by-function-type/' + type);
    }

    getExpressDeliveryServices(): Observable<Service[]> {
        return this.getServicesByFunctionType('express_delivery');
    }

    getPaginatedOrders(page: number = 0, size: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search?: string, userId?: string):
        Observable<PaginatedData<ServiceOrder>> {
        let params = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }
        if (search) {
            params['search'] = search;
        }

        if (userId) {
            params['userId'] = userId;
        }

        return this._httpClient.get<PaginatedData<ServiceOrder>>('api/service/paginated-orders', {params}).pipe(
            tap((response: PaginatedData<ServiceOrder>) => {
                this._paginatedOrdersSubject.next(response);
            })
        );
    }

    setServiceOrderStatus(id: string, status: ServiceOrderStatus, action?: RefundAction, reason?: string): Observable<ServiceOrder> {
        return this.paginatedOrders$.pipe(
            take(1),
            switchMap(orders => this._httpClient.patch<ServiceOrder>('api/service/order/' + id + '/status', {
                status: status,
                reason: reason,
                action: action,
            }).pipe(
                map((updatedServiceOrder) => {
                    const index = orders.data.findIndex(item => item.uuid === id);
                    orders.data[index] = updatedServiceOrder;
                    this._paginatedOrdersSubject.next(orders);
                    return updatedServiceOrder;
                })
            ))
        );
    }

    saveService(service: Service): Observable<Service> {
        return this.services$.pipe(
            take(1),
            switchMap(services => this._httpClient.post<Service>('api/service', service).pipe(
                map((updatedService) => {
                    const index = services.findIndex(item => item.uuid === service.uuid);
                    if (index !== -1) {
                        services[index] = updatedService;
                    } else {
                        services = services.concat(updatedService);
                    }
                    services.sort((a, b) => (+(a.type > b.type) || +(a.type === b.type) - 1) ||
                        (+(a.title > b.title) || +(a.title === b.title) - 1));
                    this._servicesSubject.next(services);
                    return updatedService;
                })
            ))
        );
    }

    deleteService(service: Service): Observable<void> {
        return this.services$.pipe(
            take(1),
            switchMap(services => this._httpClient.delete<void>('api/service/by-uuid/' + service.uuid,).pipe(
                map(() => {
                    const index = services.findIndex(item => item.uuid === service.uuid);
                    services.splice(index, 1);
                    this._servicesSubject.next(services);
                    return;
                })
            ))
        );
    }

    getService(id: string): Service {
        let services: Service[];
        this.services$.subscribe(s => services = s);
        return services?.find(service => service.uuid === id);
    }

    getUnassignedServiceFunctions(serviceUuid: string): Observable<ServiceFunction[]> {
        let params = {};
        if (serviceUuid) {
            params['service'] = serviceUuid;
        }
        return this._httpClient.get<ServiceFunction[]>('api/service/unused-service-functions', {params: params});
    }


    private getServiceByType(type?: string): Service[] {
        if (!this._services) {
            return [];
        }

        if (!type) {
            return cloneDeep(this._services);
        }

        return cloneDeep(this._services.filter(service => service.type === type));
    }

    getPendingPayoutsCount(): Observable<number> {
        return this._httpClient.get<number>('api/service/pending-count').pipe(
            tap((count) => {
                this._pendingCount.next(count);
            })
        );
    }
}
